import { useInteractionStore } from "../stores/useInteraction";
import type { CameraOverride } from "../stores/useInteraction";

export type InteractiveOptions = {
  id: string;
  promptText?: string;
  hudText?: string | (() => string);
  radius?: number;
  onInteract?: () => void;
  cameraConfig?: CameraOverride | null;
  getCameraTarget?: () => [number, number, number] | null;
};

export class Interactive {
  public readonly id: string;
  public readonly promptText: string;
  public readonly hudText?: string | (() => string);
  public readonly radius: number;
  private readonly onInteract?: () => void;
  private readonly cameraConfig?: CameraOverride | null;
  private readonly getCameraTarget?: () => [number, number, number] | null;
  private isWithinRadius = false;
  private lastDistance = Infinity;

  private static readonly PROMPT_RADIUS = 5;

  private toggleInteraction(forceEnter: boolean = false): boolean {
    const state = useInteractionStore.getState();

    if (state.activePromptId !== this.id) return false;
    if (!this.isWithinRadius) return false;
    if (forceEnter && state.playerHidden) return false;

  const nextHidden = state.togglePlayerHidden();

    let overrideConfig: CameraOverride | null = null;

    if (this.cameraConfig && nextHidden) {
      overrideConfig = { ...this.cameraConfig };
      if (!overrideConfig.target && this.getCameraTarget) {
        const target = this.getCameraTarget();
        if (target) {
          overrideConfig.target = target;
        }
      }
    }

    state.setCameraOverride(nextHidden, overrideConfig);

    if (nextHidden) {
      const message =
        typeof this.hudText === "function" ? this.hudText() : this.hudText;

      if (message) {
        state.showHudMessage(message, Infinity);
      }
    } else {
      state.clearHudMessage();
    }

    if (this.onInteract) {
      this.onInteract();
    }

    if (this.isWithinRadius && this.lastDistance <= Interactive.PROMPT_RADIUS) {
      state.activatePrompt(this.id, nextHidden ? "Cerrar [E]" : this.promptText);
    } else if (state.activePromptId === this.id && !nextHidden) {
      state.deactivatePrompt(this.id);
    }

    return true;
  }

  private readonly handleKeyDown = (event: KeyboardEvent) => {
    if (event.code !== "KeyE" && event.key?.toLowerCase() !== "e") return;
    if (!this.isWithinRadius) return;

    this.toggleInteraction();
  };

  constructor({
    id,
    promptText = "Interactuar [E]",
  hudText = "",
    radius = 5,
    onInteract,
    cameraConfig = null,
    getCameraTarget,
  }: InteractiveOptions) {
    this.id = id;
    this.promptText = promptText;
  this.hudText = hudText;
    this.radius = radius;
    this.onInteract = onInteract;
    this.cameraConfig = cameraConfig;
    this.getCameraTarget = getCameraTarget;

    window.addEventListener("keydown", this.handleKeyDown);
  }

  public enterInteraction(): boolean {
    return this.toggleInteraction(true);
  }

  public setWithinRadius(within: boolean, distance?: number) {
    const state = useInteractionStore.getState();
    const { activatePrompt, deactivatePrompt, playerHidden, activePromptId } = state;

    if (!within) {
      this.isWithinRadius = false;
      this.lastDistance = Infinity;
      if (activePromptId === this.id) {
        deactivatePrompt(this.id);
      }
      return;
    }

    this.isWithinRadius = true;
    this.lastDistance = distance ?? this.radius;

    if (this.lastDistance > Interactive.PROMPT_RADIUS) {
      if (activePromptId === this.id) {
        deactivatePrompt(this.id);
      }
      return;
    }

    const label = playerHidden ? "Cerrar [E]" : this.promptText;
    activatePrompt(this.id, label);
  }

  public dispose() {
    window.removeEventListener("keydown", this.handleKeyDown);

    if (this.isWithinRadius) {
      const { deactivatePrompt } = useInteractionStore.getState();
      deactivatePrompt(this.id);
      this.isWithinRadius = false;
    }
  }
}
